import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { Employee, fetchEmployees } from '../../api/employees';
import { fetchLocations, type Location } from '../../api/locations';
import { runSyncJob } from '../../api/syncData';

interface EmployeesPanelProps {
  onSelect?: (id: string) => void;
}

interface EmployeesState {
  records: Employee[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const EmployeesPanel: FC<EmployeesPanelProps> = ({ onSelect }) => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<EmployeesState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [locations, setLocations] = useState<Location[]>([]);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );

    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, locationFilter]);

  useEffect(() => {
    // Load locations for the filter dropdown
    let mounted = true;
    fetchLocations({ page: 1, limit: 200 })
      .then((res) => {
        if (!mounted) return;
        setLocations(res.data || []);
      })
      .catch(() => { })
      .finally(() => { });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchEmployees({
      page,
      limit,
      search: debouncedSearch || undefined,
      role: (roleFilter || undefined) as any,
      locationId: locationFilter || undefined,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setState({
          records: response.data || [],
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load employees.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch, refreshCounter, roleFilter, locationFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  const renderName = (employee: Employee) => {
    const name = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim();
    return name.length ? name : employee.username;
  };

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setError(null);
    try {
      await runSyncJob('employees');
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync employees.';
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, or phone..."
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full sm:w-60 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">All roles</option>
            <option value="COMPANY_OWNER">Company Owner</option>
            <option value="STORE_MANAGER">Store Manager</option>
            <option value="RETAIL_PLANNER">Retail Planner</option>
            <option value="MASTER">Master</option>
            <option value="WAREHOUSE_MANAGER">Warehouse Manager</option>
            <option value="DIRECTOR">Director</option>
            <option value="TECHNICIAN">Technician</option>
            <option value="ADVISOR">Advisor</option>
            <option value="ADMIN">Admin</option>
          </select>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="w-full sm:w-72 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">All locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={String(loc.erpId)}>
                {loc.name}
              </option>
            ))}
          </select>
          {(roleFilter || locationFilter) && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
                setRoleFilter('');
                setLocationFilter('');
                setPage(1);
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from ERP'}
        </button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Username</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Mobile</th>
              <th className="px-5 py-3 font-semibold">Role</th>
              <th className="px-5 py-3 font-semibold">Locations</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  Loading employees...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={6}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={6}>
                  No employees found.
                </td>
              </tr>
            ) : (
              state.records.map((employee) => (
                <tr key={employee.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{renderName(employee)}</td>
                  <td className="px-5 py-3 text-slate-500">{employee.username}</td>
                  <td className="px-5 py-3 text-slate-500">{employee.email ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{employee.mobileNumber ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{employee.typeKey ?? '—'}</td>
                  <td
                    className="px-5 py-3 text-slate-500"
                    title={
                      Array.isArray(employee.locations)
                        ? employee.locations.length === 0
                          ? 'All locations'
                          : employee.locations.map((l) => l.name).join(', ')
                        : 'All locations'
                    }
                  >
                    {Array.isArray(employee.locations)
                      ? employee.locations.length === 0
                        ? 'All locations'
                        : `${employee.locations.length} location${employee.locations.length === 1 ? '' : 's'}`
                      : 'All locations'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-primary transition hover:bg-primary/10"
                      onClick={() => onSelect?.(employee.id)}
                    >
                      👁️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            employees
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} (20 / page)</span>
        }
      />
    </div>
  );
};

export default EmployeesPanel;
